import { PositionSide } from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import { createSelector } from '@reduxjs/toolkit'

import { selectOffchainPricesInfo, selectPrices } from 'state/prices/selectors'
import { RootState } from 'state/store'
import { unserializeTradeInputs } from 'utils/futures'

import { PerpsProvider, TradingModes } from '../types'

export const selectPerpsProvider = (state: RootState) => state.futures.preferences.selectedProvider

export const selectPerpsProviderState = createSelector(
	selectPerpsProvider,
	(state: RootState) => state,
	(type, state) => {
		switch (type) {
			case PerpsProvider.SNX_V2:
				return state.snxPerpsV2
			case PerpsProvider.SNX_V3:
				return state.snxPerpsV3
			case PerpsProvider.PERENNIAL_V2:
				return state.perennialV2
			default:
				return state.snxPerpsV3
		}
	}
)

export const selectTradingMode = createSelector(
	(state: RootState) => state.futures.preferences,
	(preferences) => preferences.tradingMode
)

export const selectLeverageSide = (state: RootState) => state.futures.tradePanel.leverageSide

export const selectLeverageInput = (state: RootState) => state.futures.tradePanel.leverageInput

export const selectOneClickTradingSelected = createSelector(
	selectTradingMode,
	(mode) => mode === TradingModes.ONE_CLICK
)

export const selectMarketAsset = createSelector(
	selectPerpsProvider,
	(state: RootState) => state.futures,
	(provider, futures) => futures.selectedMarketAsset[provider]
)

export const selectMarketIndexPrice = createSelector(
	selectMarketAsset,
	selectPrices,
	(marketAsset, prices) => {
		const price = prices[marketAsset]
		return price?.offChain ?? price?.onChain ?? wei(0)
	}
)

export const selectMarketOnchainPrice = createSelector(
	selectMarketAsset,
	selectPrices,
	(marketAsset, prices) => {
		const price = prices[marketAsset]
		return price?.onChain ?? wei(0)
	}
)

export const selectMarketPriceInfo = createSelector(
	selectMarketAsset,
	selectOffchainPricesInfo,
	(asset, pricesInfo) => {
		if (!asset || !pricesInfo[asset]) return
		return pricesInfo[asset]
	}
)

export const selectSelectedSubAccountAddress = (state: RootState) =>
	state.futures.delegation.selectedSubAccountAddress

export const selectAbstractionEthBalance = (state: RootState) =>
	state.futures.oneClickTrading.ethBalance

export const selectAbstractionUsdcBalance = (state: RootState) =>
	state.futures.oneClickTrading.usdcBalance

export const selectAbstractionUsdcEngineAllowance = (state: RootState) =>
	state.futures.oneClickTrading.usdcEngineAllowance

export const selectUserInfoCollapsed = (state: RootState) =>
	state.futures.preferences.userInfoCollapsed

export const selectUserInfoShowAllMarkets = (state: RootState) =>
	state.futures.preferences.userInfoShowAllMarkets
export const selectEditPositionInputs = (state: RootState) => state.futures.editPositionInputs

export const selectOpenMarketDropdownList = (state: RootState) =>
	state.app.showModal?.type === 'manage_markets'

export const selectPortfolioChartType = (state: RootState) =>
	state.futures.preferences.portfolioChartType

export const selectSelectedPortfolioTimeframe = (state: RootState) =>
	state.futures.dashboard.selectedPortfolioTimeframe

export const selectRawTradePanelInputs = (state: RootState) => state.futures.tradePanel.inputs

export const selectTradePanelOrderPrice = createSelector(
	selectRawTradePanelInputs,
	(inputs) => inputs.orderPrice.price ?? ''
)

export const selectTradeOrderType = createSelector(selectRawTradePanelInputs, (inputs) => {
	return inputs.orderType
})

export const selectTradePanelSlTpInputs = createSelector(
	selectRawTradePanelInputs,
	({ stopLossPrice, takeProfitPrice }) => ({
		stopLossPrice: stopLossPrice || '',
		takeProfitPrice: takeProfitPrice || '',
		stopLossPriceWei: stopLossPrice && stopLossPrice !== '' ? wei(stopLossPrice) : undefined,
		takeProfitPriceWei:
			takeProfitPrice && takeProfitPrice !== '' ? wei(takeProfitPrice) : undefined,
	})
)

export const selectTradePanelInputs = createSelector(
	selectLeverageSide,
	selectRawTradePanelInputs,
	(side, tradeInputs) => {
		const inputs = unserializeTradeInputs(tradeInputs)
		const deltas = {
			susdSizeDelta: side === PositionSide.LONG ? inputs.susdSize : inputs.susdSize.neg(),
			nativeSizeDelta: side === PositionSide.LONG ? inputs.nativeSize : inputs.nativeSize.neg(),
		}
		return {
			...inputs,
			...deltas,
			susdSizeString: tradeInputs.susdSize.toString(),
			nativeSizeString: tradeInputs.nativeSize.toString(),
		}
	}
)

export const selectIsConditionalOrder = createSelector(
	selectTradePanelInputs,
	({ orderType }) => orderType === 'limit' || orderType === 'stop'
)

export const selectSlTpTradeInputs = createSelector(
	selectRawTradePanelInputs,
	({ stopLossPrice, takeProfitPrice }) => ({
		stopLossPrice: stopLossPrice || '',
		takeProfitPrice: takeProfitPrice || '',
		stopLossPriceWei: stopLossPrice && stopLossPrice !== '' ? wei(stopLossPrice) : undefined,
		takeProfitPriceWei:
			takeProfitPrice && takeProfitPrice !== '' ? wei(takeProfitPrice) : undefined,
	})
)

export const selectClosePositionOrderInputs = createSelector(
	(state: RootState) => state.futures,
	(futures) => {
		return futures.closePositionOrderInputs
	}
)
